import { Button } from "@heroui/react";
import { useEffect } from "react";
import { useShallow } from "zustand/shallow";

import { useBoundStore } from "@/app/store";
import { EncryptionSetupProgress } from "@/features/encryption/components/EncryptionSetupProgress";
import { EncryptionSetupForm } from "@/features/encryption/components/EncryptionSetupForm";
import { useSetupEncryptionForm } from "@/features/encryption/hooks/useSetupEncryptionForm";
import { useSetupEncryption } from "@/features/encryption/hooks/useSetupEncryption";
import { PageDataPlaceholder } from "@/shared/components/ui/PageDataPlaceholder";
import { LockIcon } from "@phosphor-icons/react";

export interface EncryptionStepProps {
  onEncryptionStateChange?: (isEncrypting: boolean) => void;
}

export function EncryptionStep({ onEncryptionStateChange }: EncryptionStepProps) {
  const { formValues, handleChange, errors, validateForm } = useSetupEncryptionForm();
  const { isSetup, progress, progressMessage, handleSetupEncryption } = useSetupEncryption();

  const { appSettings } = useBoundStore(useShallow((state) => ({ appSettings: state.settings.app })));
  const isEncryptionEnabled = appSettings.encryption;

  useEffect(() => {
    onEncryptionStateChange?.(isSetup);
  }, [isSetup, onEncryptionStateChange]);

  const handleSetupEncryptionClick = async () => {
    if (!validateForm()) return;
    await handleSetupEncryption(formValues.password);
  };

  if (isEncryptionEnabled) {
    return (
      <PageDataPlaceholder
        description="Your data is protected with end-to-end encryption. All your notes, todos, tags, and folders are encrypted with your master password."
        icon={<LockIcon />}
        title="Encryption Active"
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {isSetup ? (
        <EncryptionSetupProgress progress={progress} progressMessage={progressMessage} />
      ) : (
        <>
          <EncryptionSetupForm
            formValues={formValues}
            errors={errors}
            isDisabled={isSetup}
            onPasswordChange={(value) => handleChange("password", value)}
            onConfirmPasswordChange={(value) => handleChange("confirmPassword", value)}
            onAcknowledgedChange={(value) => handleChange("acknowledged", value)}
            onAcknowledgedLimitedFunctionalityChange={(value) =>
              handleChange("acknowledgedLimitedFunctionality", value)
            }
          />
          <Button color="primary" isDisabled={isSetup} onPress={handleSetupEncryptionClick}>
            Setup Encryption
          </Button>
        </>
      )}
    </div>
  );
}
